import { getToken } from "../storage/authStorage";
import { AccountTreeResponseDTO } from "../models/AccountTreeDTOs";
import { API_BASE_URL } from "../../../config/api";

class APIError extends Error {
  public status: number;
  public data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export interface FetchManagementAccountTreeOptions {
  accountType: string;
  search?: string;
  limit?: number;
  offset?: number;
  selectedAccountId?: string;
  excludeAccountId?: string;
}

export const fetchManagementAccountTree = async (
  options: FetchManagementAccountTreeOptions,
): Promise<AccountTreeResponseDTO> => {
  const {
    accountType,
    search,
    limit = 50,
    offset = 0,
    selectedAccountId,
    excludeAccountId,
  } = options;

  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const params = new URLSearchParams();
    params.append("account_type", accountType);

    if (search) {
      params.append("search", search);
      params.append("limit", limit.toString());
      params.append("offset", offset.toString());
    } else if (selectedAccountId) {
      params.append("selected_account_id", selectedAccountId);
    }

    if (excludeAccountId) {
      params.append("exclude_account_id", excludeAccountId);
    }

    const response = await fetch(
      `${API_BASE_URL}/api/gl-maps/for-management/tree?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch management account tree: ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching management account tree:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};
